/**
 * A control at rest.
 *
 * The one definition of what a white button, chip or field is made of, so the
 * app, `@majourneys/ui` and `@majourneys/discover` cannot each carry their own
 * near-copy of it — which is exactly what they did until now, at three
 * different opacities.
 *
 * A control is **lit white with one navy hairline**. It never wears the pewter
 * band: the band is a gradient *border* and belongs to a surface you read — a
 * panel, a card, the dropdown — not to a thing you press (owner ruling,
 * 2026-09-05). `CONTROL_LIT` is a gradient *fill*, which is a different
 * question and does not touch that rule.
 */

/**
 * Opaque on purpose. The flat fill this replaces was 92% white, and a gradient
 * behind a translucent fill takes on whatever sits under the surface — so the
 * same control came out one colour in a modal and another on a page.
 */
export const CONTROL_LIT = "linear-gradient(180deg, #FFFFFF 0%, #F7F7FB 100%)";

/** The hairline around it. Darkens when the control is open or chosen. */
export const CONTROL_EDGE = "rgba(17,14,57,0.16)";
export const CONTROL_EDGE_ON = "rgba(17,14,57,0.55)";

/*
 * The soft bevel (owner, 2026-10-25, picked on the web's /card-shadow-lab):
 * a control's face is shaded top to bottom, with a light line inside its top
 * edge and a faint shade inside its bottom one, so it reads as a soft raised
 * object. Pressed, it sinks: the shading flips and one shade sits along the
 * top inside edge. The edge stays a plain hairline, so the 2026-09-05 rule
 * (no gradient border on controls) still holds: this is all on the face.
 * Chosen chips take the navy face and still cast no drop shadow.
 *
 * The app keeps the same values in apps/mobile/src/shared/theme/tokens.ts.
 */
export const BEVEL_NAVY_FACE = "linear-gradient(180deg, #2E2878 0%, #1A1454 50%, #110E39 100%)";
export const BEVEL_NAVY_FACE_DOWN = "linear-gradient(180deg, #110E39 0%, #18144A 50%, #221D63 100%)";
export const BEVEL_WHITE_FACE = "linear-gradient(180deg, #FFFFFF 0%, #F1F2F5 100%)";
export const BEVEL_WHITE_FACE_DOWN = "linear-gradient(180deg, #ECEDF2 0%, #F7F7FA 100%)";

export const BEVEL_INSET_WHITE = "inset 0 1.5px 0 #FFFFFF, inset 0 -1.5px 0 rgba(17,14,57,0.06)";
export const BEVEL_INSET_NAVY = "inset 0 1.5px 0 rgba(255,255,255,0.22), inset 0 -1.5px 0 rgba(0,0,0,0.25)";
export const BEVEL_INSET_DOWN = "inset 0 2px 4px rgba(17,14,57,0.12)";
export const BEVEL_INSET_DOWN_NAVY = "inset 0 2px 4px rgba(0,0,0,0.35)";

/**
 * A chosen chip: the navy face with no light along its top, just one navy
 * hairline (owner, 2026-10-25, variant A). On a chip the 1.5px top light read
 * as a stray white edge; buttons keep it, chips do not. Still no drop shadow.
 */
export const BEVEL_CHIP_ON = "0 0 0 1px #110E39";
